import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CalendarDays, CheckCircle2, Users, FileText, ArrowRight, Flame } from "lucide-react";
import { format, parseISO } from "date-fns";

function StatCard({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <div className="bm-card p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-4">
        <span className="overline">{label}</span>
        <Icon className="w-4 h-4 text-[#8ba394]" />
      </div>
      <div className="font-display text-4xl font-black leading-none" style={{ color: accent || "#eae6db" }}>
        {value}
      </div>
      {sub && <div className="text-xs text-[#8ba394] mt-2">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        setStats(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading)
    return (
      <div className="overline" data-testid="dashboard-loading">Loading dashboard…</div>
    );
  if (!stats) return <div data-testid="dashboard-empty">Unable to load stats.</div>;

  const todayLabel = format(parseISO(stats.today), "EEEE, MMMM d");

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Operations Console</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-none">
            Today · <span className="text-[#e56b42]">{todayLabel}</span>
          </h1>
        </div>
        <Link to="/schedule" className="bm-btn bm-btn-primary" data-testid="dashboard-open-schedule">
          Open Today's Route <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Jobs Today"
          value={stats.jobs_today}
          sub={`${stats.jobs_completed_today} completed`}
          icon={Flame}
          accent="#c83e2d"
          testid="stat-jobs-today"
        />
        <StatCard
          label="This Week"
          value={stats.jobs_this_week}
          sub="scheduled jobs"
          icon={CalendarDays}
          testid="stat-jobs-week"
        />
        <StatCard
          label="Customers"
          value={stats.customers_count}
          sub="in your book"
          icon={Users}
          testid="stat-customers"
        />
        <StatCard
          label="Open Invoices"
          value={stats.open_invoices}
          sub="draft or sent"
          icon={FileText}
          testid="stat-open-invoices"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Upcoming */}
        <div className="bm-card p-5 lg:col-span-2" data-testid="upcoming-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="overline">Upcoming · Next 7 Days</h2>
            <Link to="/schedule" className="text-xs text-[#8ba394] hover:text-[#eae6db]">
              View all →
            </Link>
          </div>
          {stats.upcoming.length === 0 ? (
            <div className="text-center py-10 text-[#8ba394] text-sm">
              No scheduled jobs in the next week.
              <div className="mt-3">
                <Link to="/schedule" className="bm-btn bm-btn-outline" data-testid="empty-schedule-cta">
                  Add a job
                </Link>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-[#233129]">
              {stats.upcoming.map((j) => (
                <div key={j.id} className="py-3 flex items-center gap-4" data-testid={`upcoming-job-${j.id}`}>
                  <div className="font-mono text-sm w-24 text-[#e56b42]">
                    {format(parseISO(j.scheduled_date), "MMM d")}
                    {j.scheduled_time && (
                      <span className="block text-[#8ba394] text-xs">{j.scheduled_time}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{j.customer_name}</div>
                    <div className="text-xs text-[#8ba394] truncate">{j.customer_address || "No address"}</div>
                  </div>
                  <span className="bm-badge bm-badge-scheduled hidden sm:inline-flex">{j.service_type}</span>
                  {j.frequency !== "once" && (
                    <span className="bm-badge bm-badge-sent hidden md:inline-flex">{j.frequency}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent customers */}
        <div className="bm-card p-5" data-testid="recent-customers-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="overline">Recent Customers</h2>
            <Link to="/customers" className="text-xs text-[#8ba394] hover:text-[#eae6db]">
              View all →
            </Link>
          </div>
          {stats.recent_customers.length === 0 ? (
            <div className="text-center py-10 text-[#8ba394] text-sm">
              No customers yet.
              <div className="mt-3">
                <Link to="/customers" className="bm-btn bm-btn-outline" data-testid="empty-customers-cta">
                  Add customer
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {stats.recent_customers.map((c) => (
                <Link
                  key={c.id}
                  to={`/customers/${c.id}`}
                  className="block bm-card bm-card-hover p-3"
                  data-testid={`recent-customer-${c.id}`}
                >
                  <div className="font-medium text-sm">{c.name}</div>
                  <div className="text-xs text-[#8ba394] truncate mt-1">
                    {c.address || c.phone || c.email || "—"}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
